import { inject, Injectable } from '@angular/core';
import { ID } from '@datorama/akita';
import { switchMap, tap } from 'rxjs/operators';

import { GqlService } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';

import {
  SpecificationCategoryModel,
  SpecificationCategoryStore,
} from './specification-category.store';
import { SpecificationModel, SpecificationStore } from '../specification/specification.store';
import {
  SpecificationSettingModel,
  SpecificationSettingStore,
} from '../specification-setting/specification-setting.store';

@Injectable({ providedIn: 'root' })
export class SpecificationCategoryService {
  private readonly gqlService = inject(GqlService);
  private readonly mainQuery = inject(MainQuery);
  private readonly overlayService = inject(OverlayService);
  private readonly specificationCategoryStore = inject(SpecificationCategoryStore);
  private readonly specificationSettingStore = inject(SpecificationSettingStore);
  private readonly specificationStore = inject(SpecificationStore);

  add(specificationCategory: SpecificationCategoryModel) {
    this.specificationCategoryStore.add(specificationCategory);
  }

  getAllSpecifications$() {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.specificationCategoryStore.setLoading(true);

        return this.gqlService.listSpecificationSettings$();
      }),
      tap(({ data, errors, success }) => {
        if (success && data) {
          const allSpecificationCategories: SpecificationCategoryModel[] = [];
          const allSpecifications: SpecificationModel[] = [];
          const allSpecificationSettings: SpecificationSettingModel[] = [];

          data.forEach((specificationCategory) => {
            const { specifications } = specificationCategory;

            allSpecificationCategories.push({
              ...specificationCategory,
              specifications: (specifications || []).map(
                (specification) => specification?.id
              ) as string[],
            });

            (specifications || []).forEach((specification) => {
              if (specification) {
                const { settings } = specification;

                allSpecifications.push({
                  ...specification,
                  settings: (settings || []).map((x) => x?.id) as string[],
                });

                (settings || []).forEach((setting) => {
                  if (setting) {
                    allSpecificationSettings.push({
                      ...setting,
                      specification_type: setting.specification_type,
                    });
                  }
                });
              }
            });
          });

          this.specificationCategoryStore.set(allSpecificationCategories);
          this.specificationStore.set(allSpecifications);
          this.specificationSettingStore.set(allSpecificationSettings);
        } else {
          this.overlayService.error(errors);
        }

        this.specificationCategoryStore.setLoading(false);
      })
    );
  }

  remove(id: ID) {
    this.specificationCategoryStore.remove(id);
  }

  update(id: string, specificationCategory: Partial<SpecificationCategoryModel>) {
    this.specificationCategoryStore.update(id, specificationCategory);
  }
}
